import { css } from "@emotion/react";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { graphql, usePaginationFragment } from "react-relay";
import { useNavigate } from "react-router";

import {
  DebouncedSearch,
  Icon,
  Icons,
  LinkButton,
  Button,
} from "@phoenix/components";
import { CanModify } from "@phoenix/components/auth";
import { StopPropagation } from "@phoenix/components/StopPropagation";
import { useViewerCanModify } from "@phoenix/contexts";
import { usePromptsFilterContext } from "@phoenix/pages/prompts/PromptsFilterProvider";
import { PromptsLabelMenu } from "@phoenix/pages/prompts/PromptsLabelMenu";

import type { PromptsTable_prompts$key } from "./__generated__/PromptsTable_prompts.graphql";
import type { PromptsTablePromptsQuery } from "./__generated__/PromptsTablePromptsQuery.graphql";
import { PromptActionMenu } from "./PromptActionMenu";
import { PromptsEmpty } from "./PromptsEmpty";

const PAGE_SIZE = 100;

type PromptsTableProps = {
  query: PromptsTable_prompts$key;
};

const pageWrapperCSS = css`
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  font-family: var(--ac-premium-font-family, 'Century Gothic', sans-serif);
`;

const headerContainerCSS = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: -0.02em;
    color: var(--global-text-color-900, #191c1d);
    margin: 0 0 6px 0;
  }

  p {
    font-size: 13px;
    color: var(--global-color-gray-600, #575e70);
    margin: 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    button, [role="button"] {
      border-radius: 9999px !important;
      padding: 8px 20px !important;
      font-weight: 500 !important;
      font-size: 14px !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 6px !important;
      height: auto !important;
    }
  }
`;

const newPromptButtonCSS = css`
  border-radius: 9999px !important;
  padding: 8px 20px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  height: auto !important;
  background-color: var(--ac-premium-teal, #00685f) !important;
  color: #ffffff !important;
  border: none !important;

  &:hover {
    background-color: var(--global-color-surface-tint, #006a61) !important;
    color: #ffffff !important;
  }
`;

const filterBarCSS = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  input, [role="searchbox"] {
    border-radius: 9999px !important;
    padding-left: 36px !important;
    background-color: var(--global-color-surface-container-lowest, #ffffff) !important;
    border: 1px solid var(--global-color-outline-variant, #bcc9c6) !important;
  }

  .sort-btn {
    border-radius: 9999px !important;
    padding: 8px 16px !important;
    border: 1px solid var(--global-color-outline-variant, #bcc9c6) !important;
    background-color: var(--global-color-surface-lowest, #ffffff) !important;
    color: var(--global-color-gray-600, #575e70) !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    height: auto !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;

    &:hover {
      background-color: var(--global-color-surface-variant, #e1e3e4) !important;
    }
  }
`;

const cardsContainerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 20px;
`;

const promptCardCSS = css`
  background-color: var(--global-color-surface-lowest, #ffffff);
  border-radius: 12px;
  border: 1px solid var(--global-color-outline-variant, #bcc9c6);
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
    border-color: rgba(0, 104, 95, 0.25);
  }

  .title-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .prompt-title {
    font-size: 16.5px !important;
    font-weight: 800 !important;
    color: #0f172a !important;
    text-shadow: 0 0 0.25px currentColor;
  }

  p.prompt-desc,
  .prompt-desc {
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #64748b !important;
    margin: 5px 0 8px 0 !important;
    line-height: 1.5 !important;
    max-width: 672px;
  }

  .prompt-meta {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #94a3b8 !important;

    span {
      font-size: 12px !important;
      font-weight: 400 !important;
      color: #94a3b8 !important;
    }

    svg {
      color: #94a3b8 !important;
      stroke: #94a3b8 !important;
      stroke-width: 2 !important;
    }
  }

  .theme--dark & {
    .prompt-title {
      color: #ffffff !important;
    }
    p.prompt-desc,
    .prompt-desc {
      color: #94a3b8 !important;
    }
    .prompt-meta {
      color: #64748b !important;
      span {
        color: #64748b !important;
      }
      svg {
        color: #64748b !important;
        stroke: #64748b !important;
      }
    }
  }

  .actions-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .action-btn-icon {
    background: transparent !important;
    border: none !important;
    padding: 6px 8px !important;
    border-radius: 6px !important;
    color: var(--global-color-gray-500, #6d7a77) !important;
    height: auto !important;

    &:hover {
      background-color: var(--global-color-surface-variant, #e1e3e4) !important;
      color: var(--global-text-color-900, #191c1d) !important;
    }
  }
`;

const labelBadgeCSS = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background-color: rgba(0, 104, 95, 0.08);
  color: var(--ac-premium-teal, #00685f);
`;

const footerCSS = css`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--global-color-outline-variant, #bcc9c6);
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--global-color-gray-600, #575e70);
  flex-shrink: 0;

  .status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--global-color-tertiary, #006b2d);
    margin-right: 6px;
    vertical-align: middle;
  }
`;

export function PromptsTable(props: PromptsTableProps) {
  "use no memo";
  const { filter, selectedPromptLabelIds, setSelectedPromptLabelIds, setFilter } = usePromptsFilterContext();
  const navigate = useNavigate();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const queryArgs = useMemo(
    () => ({
      filter: filter.trim() ? { value: filter, col: "name" as const } : null,
      labelIds:
        selectedPromptLabelIds.length > 0 ? selectedPromptLabelIds : null,
    }),
    [filter, selectedPromptLabelIds]
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } =
    usePaginationFragment<PromptsTablePromptsQuery, PromptsTable_prompts$key>(
      graphql`
        fragment PromptsTable_prompts on Query
        @refetchable(queryName: "PromptsTablePromptsQuery")
        @argumentDefinitions(
          after: { type: "String", defaultValue: null }
          first: { type: "Int", defaultValue: 100 }
          filter: { type: "PromptFilter", defaultValue: null }
          labelIds: { type: "[ID!]", defaultValue: null }
        ) {
          prompts(first: $first, after: $after, filter: $filter, labelIds: $labelIds)
            @connection(key: "PromptsTable_prompts") {
            edges {
              prompt: node {
                id
                name
                description
                version {
                  createdAt
                }
                labels {
                  id
                  name
                  color
                }
              }
            }
          }
        }
      `,
      props.query
    );

  useEffect(() => {
    startTransition(() => {
      refetch(queryArgs, {
        fetchPolicy: "store-and-network",
      });
    });
  }, [refetch, queryArgs]);

  const tableData = useMemo(
    () =>
      data.prompts.edges.map((edge) => {
        return {
          lastUpdatedAt: edge.prompt.version.createdAt,
          ...edge.prompt,
        };
      }),
    [data]
  );

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isLoadingNext &&
          hasNext
        ) {
          loadNext(PAGE_SIZE, { UNSTABLE_extraVariables: queryArgs });
        }
      }
    },
    [hasNext, isLoadingNext, loadNext, queryArgs]
  );

  const canModify = useViewerCanModify();

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      const datePart = d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timePart = d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart} · ${timePart}`;
    } catch (e) {
      return dateStr;
    }
  };

  const isEmpty = tableData.length === 0;

  return (
    <div css={pageWrapperCSS}>
      {/* Page Header */}
      <div css={headerContainerCSS}>
        <div>
          <h2>Prompts</h2>
          <p>Manage your prompt templates and versions</p>
        </div>
        <div className="header-actions">
          <PromptsLabelMenu
            selectedLabelIds={selectedPromptLabelIds}
            onSelectionChange={setSelectedPromptLabelIds}
          />
          <CanModify>
            <LinkButton
              leadingVisual={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              variant="primary"
              to="/playground"
              css={newPromptButtonCSS}
            >
              New Prompt
            </LinkButton>
          </CanModify>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div css={filterBarCSS}>
        <div style={{ flex: "1 1 100%", width: "100%" }}>
          <DebouncedSearch
            aria-label="Search prompts by name"
            onChange={setFilter}
            defaultValue={filter}
            placeholder="Search prompts by name..."
          />
        </div>
      </div>

      {/* Prompt Cards */}
      {isEmpty ? (
        <PromptsEmpty />
      ) : (
        <div
          css={cardsContainerCSS}
          onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
          ref={tableContainerRef}
        >
          {tableData.map((prompt) => (
            <div
              key={prompt.id}
              css={promptCardCSS}
              onClick={() => navigate(`${prompt.id}`)}
            >
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "space-between", gap: "12px" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div className="title-row">
                    <span className="prompt-title">{prompt.name}</span>
                    {prompt.labels.length === 0 ? (
                      <span css={labelBadgeCSS}>—</span>
                    ) : (
                      prompt.labels.map((label) => (
                        <span key={label.id} css={labelBadgeCSS}>
                          <span className="material-symbols-outlined" style={{ fontSize: "14px", marginRight: "4px" }}>
                            fact_check
                          </span>
                          {label.name}
                        </span>
                      ))
                    )}
                  </div>
                  <p className="prompt-desc">
                    {prompt.description || "—"}
                  </p>
                  <div className="prompt-meta">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" style={{ marginRight: "4px" }}>
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 15 15" />
                    </svg>
                    <span>Updated: {formatDate(prompt.lastUpdatedAt)}</span>
                  </div>
                </div>

                <div className="actions-container">
                  <StopPropagation>
                    <LinkButton
                      leadingVisual={
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      }
                      size="S"
                      aria-label="Open in playground"
                      to={`/playground?promptId=${encodeURIComponent(prompt.id)}`}
                      css={css`
                        background-color: rgba(0, 104, 95, 0.08) !important;
                        color: var(--ac-premium-teal, #00685f) !important;
                        border-radius: 6px !important;
                        font-weight: 500 !important;
                        font-size: 12px !important;
                        padding: 6px 12px !important;
                        border: none !important;
                        height: auto !important;

                        &:hover {
                          background-color: var(--ac-premium-teal, #00685f) !important;
                          color: #ffffff !important;
                        }
                      `}
                    >
                      Playground
                    </LinkButton>
                  </StopPropagation>
                  <StopPropagation>
                    <Button
                      size="S"
                      leadingVisual={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      }
                      onPress={() => navigate(`${prompt.id}`)}
                      aria-label="Edit prompt"
                      className="action-btn-icon"
                    />
                  </StopPropagation>
                  {canModify && (
                    <PromptActionMenu
                      promptId={prompt.id}
                      onDeleted={() => {
                        refetch(queryArgs, { fetchPolicy: "network-only" });
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
